import type { Image, ImageFormat, ImageFormats, RichText } from '@eneco/cms-types';
import { asRichText } from '@eneco/cms-types';
import { createImageUrlBuilder } from '@sanity/image-url';

import type {
  LocalizedArrayEntry,
  PortableTextBlock,
  PortableTextSpan,
  SanityImage,
  SanityImageWithAlt,
} from './sanity-types';

const isObject = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null;

/**
 * `@sanity/image-url` THROWS on a ref that doesn't match its expected shape
 * (`image-<id>-<w>x<h>-<fmt>`), rather than returning gracefully — so this
 * validates and extracts width/height up front. A ref failing this check
 * means `toImage` returns `undefined` without ever calling the builder,
 * matching the "never throw" adapter contract.
 */
const parseAssetRef = (ref: string): { width: number; height: number } | undefined => {
  const match = /^image-[a-zA-Z0-9]+-(\d+)x(\d+)-[a-zA-Z]+$/.exec(ref);
  if (!match) return undefined;
  return { width: Number(match[1]), height: Number(match[2]) };
};

/**
 * Alt text from either shape the CMS schema uses: `Image`'s internationalized array (Eneco's
 * `alt` text plugin) or `focalPointImage`'s plain string (`HeroCard.brandPrimaryImage`,
 * `ContentCard.image`, …). NOTE (simplification): for the array shape, picks the first non-empty entry
 * rather than the requesting locale's — `toImage` has no locale parameter on the shared `CmsAdapter`
 * contract. Fine while pages are fetched one-locale-at-a-time; revisit if a page ever mixes alt text
 * across languages.
 */
const firstLocalizedValue = (entries: unknown): string | undefined => {
  if (typeof entries === 'string') return entries.length > 0 ? entries : undefined;
  return Array.isArray(entries)
    ? (entries as LocalizedArrayEntry[]).find(entry => Boolean(entry?.value))?.value
    : undefined;
};

export interface ImageMapperConfig {
  projectId: string;
  dataset: string;
}

/**
 * Each named format's breakpoint, as the Sitecore adapter maps them (`mappers.ts#breakpointForFormat` there),
 * widest first: a `<picture>` takes the first source whose `min-width` query matches, so a narrower one listed
 * first would win at every width. Sitecore sends its formats in that order.
 */
const FORMAT_BREAKPOINTS: Array<[Exclude<keyof ImageFormats, 'default'>, string]> = [
  ['widescreen', 'xxl'],
  ['extraLarge', 'xl'],
  ['large', 'lg'],
  ['medium', 'md'],
  ['small', 'sm'],
];

/** A crop or hotspot value within 0–1. The URL builder turns a missing or out-of-range one into a broken `rect`. */
const fraction = (value: unknown, fallback: number): number =>
  typeof value === 'number' && Number.isFinite(value) ? Math.min(Math.max(value, 0), 1) : fallback;

/** The editor's crop, clamped; none when it would leave nothing of the image. */
const cropOf = (crop: SanityImage['crop']) => {
  if (!crop) return undefined;
  const clamped = {
    top: fraction(crop.top, 0),
    bottom: fraction(crop.bottom, 0),
    left: fraction(crop.left, 0),
    right: fraction(crop.right, 0),
  };
  return clamped.left + clamped.right < 1 && clamped.top + clamped.bottom < 1 ? clamped : undefined;
};

const hotspotOf = (hotspot: SanityImage['hotspot']) =>
  hotspot
    ? {
        x: fraction(hotspot.x, 0.5),
        y: fraction(hotspot.y, 0.5),
        width: fraction(hotspot.width, 1),
        height: fraction(hotspot.height, 1),
      }
    : undefined;

/** The widest an image is served without formats: Sitecore's widest format, so neither lane sends more. */
const MAX_WIDTH = 1920;

export const createToImage = ({ projectId, dataset }: ImageMapperConfig) => {
  const builder = createImageUrlBuilder({ projectId, dataset });

  return (raw: unknown, componentFormats?: ImageFormats): Image | undefined => {
    if (!isObject(raw)) return undefined;
    // Accept either a bare Sanity image ({ asset }) or Eneco's `imageWithAlt`
    // wrapper ({ image: { asset }, alt: [...] }).
    const wrapper = raw as SanityImageWithAlt;
    const image = (isObject(wrapper.image) ? wrapper.image : raw) as SanityImage;
    const ref = image.asset?._ref;
    if (typeof ref !== 'string' || ref.length === 0) return undefined;
    const dimensions = parseAssetRef(ref);
    if (!dimensions) return undefined;

    // The image CDN applies the editor's crop, and crops to a format around the hotspot.
    const imageCrop = cropOf(image.crop);
    const hotspot = hotspotOf(image.hotspot);
    const source = builder
      .image({
        asset: { _ref: ref },
        ...(imageCrop ? { crop: imageCrop } : {}),
        ...(hotspot ? { hotspot } : {}),
      })
      .auto('format');
    // Both levels, because both shapes exist in the dataset. `imageWithAlt` models alt as a
    // sibling of `image`; the CMS schema's own `image` object carries its own `alt` instead,
    // and the MKB home page uses that one exclusively — every image there rendered `alt=""`.
    //
    // Not only an accessibility bug. Sanity's documented way to make an image click-to-editable is
    // stega inside the `alt` attribute (there is no text node in an `<img>` to hide it in), so an
    // empty alt is also an image the editor cannot click in Presentation.
    const alt = firstLocalizedValue(wrapper.alt) ?? firstLocalizedValue((image as Record<string, unknown>).alt);

    // The part of the original the editor's crop keeps.
    const width = Math.round(dimensions.width * (1 - (imageCrop?.left ?? 0) - (imageCrop?.right ?? 0)));
    const height = Math.round(dimensions.height * (1 - (imageCrop?.top ?? 0) - (imageCrop?.bottom ?? 0)));

    // The formats the CMS gives the image's field (`image-formats.ts`) before the component's own.
    const formats = image.formats ?? componentFormats;
    const named = Object.entries(formats ?? {}).filter((entry): entry is [keyof ImageFormats, ImageFormat] =>
      Boolean(entry[1]),
    );
    if (named.length > 0) {
      // As the Sitecore adapter builds an image from its formats (`fromFormats` there).
      // A format no larger than the pixels the image has, in the format's shape: a bigger request only
      // adds bytes, not detail.
      const fitted = (format: ImageFormat): ImageFormat => {
        const scale = Math.min(1, width / format.width, height / format.height);
        return { width: Math.round(format.width * scale), height: Math.round(format.height * scale) };
      };
      // Sitecore crops its own media items to a format, and a Frontify image only around its focal
      // point; a Frontify image without one it scales to fit inside the format, keeping its shape.
      const scalesToFit = image.assetSource === 'frontify' && !hotspot;
      // The image in its own shape, as large as fits inside the format. Width alone: given a height
      // too, the URL builder cuts the image to that shape.
      const scaled = (format: ImageFormat): ImageFormat => {
        const scale = Math.min(1, format.width / width, format.height / height);
        return { width: Math.round(width * scale), height: Math.round(height * scale) };
      };
      const served = (format: ImageFormat) => (scalesToFit ? scaled(format) : fitted(format));
      const crop = (format: ImageFormat) => {
        const size = served(format);
        return scalesToFit
          ? source.width(size.width).fit('max').url()
          : source.width(size.width).height(size.height).fit('crop').url();
      };
      const primary = formats?.default ?? formats?.small ?? named[0][1];
      const sources = Object.fromEntries(
        FORMAT_BREAKPOINTS.flatMap(([name, breakpoint]) => {
          const format = formats?.[name];
          return format ? [[breakpoint, crop(format)]] : [];
        }),
      );
      return {
        src: crop(primary),
        ...(alt ? { alt } : {}),
        width: primary.width,
        height: primary.height,
        aspectRatio: `${primary.width}/${primary.height}`,
        ...(Object.keys(sources).length > 0 ? { sources } : {}),
        srcSet: named.map(([, format]) => `${crop(format)} ${served(format).width}w`).join(', '),
      };
    }

    return {
      src: source.width(Math.min(width, MAX_WIDTH)).fit('max').url(),
      ...(alt ? { alt } : {}),
      width,
      height,
      aspectRatio: `${width}/${height}`,
    };
  };
};

const escapeHtml = (text: string): string =>
  text.replaceAll('&', '&amp;').replaceAll('<', '&lt;').replaceAll('>', '&gt;');

const escapeAttr = (value: string): string =>
  escapeHtml(value).replaceAll('"', '&quot;');

type MarkDef = NonNullable<PortableTextBlock['markDefs']>[number];
type BlockChild = NonNullable<PortableTextBlock['children']>[number];

/** Hands a rich text image to the adapter's image mapper, which knows the project and the dataset. */
export type RichTextImageMapper = (raw: unknown) => Image | undefined;

interface RichTextOptions {
  /** Without it, an image between paragraphs is left out. */
  toImage?: RichTextImageMapper;
}

/** ` name="value"`, escaped, or nothing for an empty or missing value. */
const attribute = (name: string, value: unknown): string =>
  typeof value === 'string' && value.length > 0 ? ` ${name}="${escapeAttr(value)}"` : '';

/** ` name="value"` for a size in pixels, or nothing for a missing one. */
const dimension = (name: string, value: unknown): string =>
  typeof value === 'number' && value > 0 ? ` ${name}="${value}"` : '';

/** The tags a mark is written in: the one that opens it and the one that closes it. */
type Tags = readonly [open: string, close: string];

/**
 * The text marks without data (decorators) the schema's rich text allows, each with the tags Sitecore's rich text
 * holds it in: Sitecore underlines with a styled `span`, which the shared `RichText` renders without its style (on the
 * live site too), and strikes an old price through with `s`.
 */
const DECORATORS = new Map<string, Tags>([
  ['strong', ['<strong>', '</strong>']],
  ['em', ['<em>', '</em>']],
  ['code', ['<code>', '</code>']],
  ['underline', ['<span style="text-decoration: underline;">', '</span>']],
  ['strike-through', ['<s>', '</s>']],
  ['sup', ['<sup>', '</sup>']],
  ['sub', ['<sub>', '</sub>']],
]);

/**
 * A chat button's tags: `<button>` with its chat topic, and the text and flag the chat opens with when the content sets
 * them, in Sitecore's order. A chat button without a topic has none, so its words stay text.
 */
const chatButtonTags = (def: MarkDef): Tags | undefined => {
  if (typeof def.chatTopic !== 'string' || def.chatTopic.length === 0) return undefined;
  const flag = typeof def.sendDisplayText === 'boolean' ? String(def.sendDisplayText) : undefined;
  const attributes = [
    attribute('data-chattopic', def.chatTopic),
    attribute('data-displaytext', def.displayText),
    attribute('data-senddisplaytext', flag),
  ].join('');
  return [`<button${attributes}>`, '</button>'];
};

/**
 * The marks with data (annotations), each with the tags Sitecore's rich text holds it in, or `undefined` to keep the
 * words as text. A link becomes `<a>` with its escaped address, and the new tab and `rel` that `links.ts` gives a link
 * that asks for one. A chat button becomes `<button>` (`chatButtonTags`), which `RichText` turns into a button that
 * opens the chat.
 */
const ANNOTATIONS = new Map<string, (def: MarkDef) => Tags | undefined>([
  [
    'link',
    def =>
      typeof def.href === 'string' && def.href.length > 0
        ? [`<a href="${escapeAttr(def.href)}"${attribute('target', def.target)}${attribute('rel', def.rel)}>`, '</a>']
        : undefined,
  ],
  ['chatButton', chatButtonTags],
]);

/**
 * The objects a block holds in its text besides words. A jump target (`richTextAnchor`) writes nothing: Sitecore holds
 * it as `<a id="…">` around a word, which the shared `RichText` renders without its id, so it is no jump target on the
 * live site either (the section a link jumps to carries the id). The words around it stay.
 */
const INLINE_OBJECTS = new Map<string, () => string>([['richTextAnchor', () => '']]);

/** What the schema allows in rich text but no content holds yet, so no Sitecore example shows how to write it. */
const NOT_RENDERED_YET = ['richTextIcon', 'richTextEmbed'];

/** The block styles Sanity's rich text allows: the paragraph, the six headings and the quote. */
const STYLES = ['normal', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'blockquote'];

/** A child's words: a span's text; an object in the text has none. */
const textOf = (child: BlockChild): string => (typeof child.text === 'string' ? child.text : '');

/** A mark a child of a block carries: its name in the child's `marks`, and the tags it is written in. */
interface Mark {
  name: string;
  tags: Tags;
}

/**
 * The marks of a child that are written, in the order the child lists them. A mark is a decorator (`DECORATORS`) or
 * the `_key` of an annotation in the block's `markDefs` (`ANNOTATIONS`). An annotation without its target, such as a
 * link without an address or a chat button without a topic, and an unknown mark are not written, so their words stay
 * text. An object in the text carries no marks.
 */
const marksOf = (child: BlockChild, markDefs: MarkDef[]): Mark[] => {
  if (INLINE_OBJECTS.has(child._type)) return [];
  return ((child as PortableTextSpan).marks ?? []).flatMap(name => {
    const def = markDefs.find(d => d._key === name);
    const tags = DECORATORS.get(name) ?? (def ? ANNOTATIONS.get(def._type)?.(def) : undefined);
    return tags ? [{ name, tags }] : [];
  });
};

/**
 * A child's own part of the text: a span's words, or what an object in the text writes (`INLINE_OBJECTS`).
 * `htmlToBlocks` (block-tools) turns a source `<br>` into a literal `\n` inside a span's text rather than a
 * `hardBreak` mark, so it must be restored here or it gets whitespace-collapsed away.
 */
const renderChild = (child: BlockChild): string =>
  INLINE_OBJECTS.get(child._type)?.() ?? escapeHtml(textOf(child)).replaceAll('\n', '<br>');

/**
 * A block's text: its children, each mark written once around the run of children that carry it, as Sitecore's rich
 * text holds a link whose words are partly formatted: one `<a>` around them all, so it stays one link, and a chat
 * button stays one button. The marks nest the way Portable Text's own serializers nest them. A mark stays open while
 * the next child carries it too, and a child's other marks open inside it: the one that runs over more of the next
 * children outermost, and marks that run as far in the order the child lists them, the first innermost. A child that
 * does not carry an open mark closes it, and every mark opened inside it.
 */
const renderChildren = (block: PortableTextBlock): string => {
  const markDefs = block.markDefs ?? [];
  const children = block.children ?? [];
  const marks = children.map(child => marksOf(child, markDefs));
  const carries = (index: number, name: string): boolean =>
    index < marks.length && marks[index].some(mark => mark.name === name);
  /** How many children in a row, from `index` on, carry the mark. */
  const runOf = (name: string, index: number): number => {
    let end = index;
    while (carries(end, name)) end += 1;
    return end - index;
  };
  const open: Mark[] = [];
  /** Closes the open marks from `position` inward, the innermost first. */
  const closeFrom = (position: number): string =>
    open
      .splice(position)
      .reverse()
      .map(mark => mark.tags[1])
      .join('');
  let html = '';
  children.forEach((child, index) => {
    const notCarried = open.findIndex(mark => !carries(index, mark.name));
    html += closeFrom(notCarried === -1 ? open.length : notCarried);
    const opening = marks[index]
      .map((mark, order) => ({ mark, order, run: runOf(mark.name, index) }))
      .filter(({ mark }) => !open.some(other => other.name === mark.name))
      .sort((left, right) => right.run - left.run || right.order - left.order)
      .map(({ mark }) => mark);
    html += `${opening.map(mark => mark.tags[0]).join('')}${renderChild(child)}`;
    open.push(...opening);
  });
  return `${html}${closeFrom(0)}`;
};

const renderBlock = (block: PortableTextBlock): string => {
  const inner = renderChildren(block);
  const style = block.style ?? 'normal';
  if (style.startsWith('h') && style.length === 2) return `<${style}>${inner}</${style}>`;
  if (block.listItem) return `<li>${inner}</li>`;
  if (style === 'blockquote') return `<blockquote>${inner}</blockquote>`;
  return `<p>${inner}</p>`;
};

/** Portable Text has no list container — each item is a sibling block carrying `listItem`. Emitting
 * them bare produces `<li>`s with no parent: invalid HTML, and browsers render them unstyled. So a
 * run of consecutive items of the same kind is wrapped once, and the wrapper closes as soon as the
 * kind changes or a non-list block appears.
 *
 * NOTE: `level` (nested lists) is not handled — a sub-list renders flat, inside its parent's
 * wrapper. That is still better-formed than today's output, and nesting is best fixed by moving to
 * `@portabletext/react` rather than growing this serializer (see `toRichText`'s note). */
const listTagFor = (block: PortableTextBlock): 'ol' | 'ul' | undefined => {
  if (!block.listItem) return undefined;
  return block.listItem === 'number' ? 'ol' : 'ul';
};

/**
 * A block in the plain style: a paragraph, not a heading, a quote or a list item. A quote is set apart by its own
 * margins, as on the live site, so it takes no separators.
 */
const isPlain = (block: PortableTextBlock): boolean => !block.listItem && (block.style ?? 'normal') === 'normal';

/**
 * A plain block without visible text: only line breaks, spaces or nothing at all.
 *
 * The HTML→Portable Text conversion makes one where Sitecore content spaced two blocks apart: a lone
 * `<br />` between them becomes a block holding `\n`, an empty paragraph (`<p>&nbsp;</p>`,
 * `<div>&nbsp;</div>`) a block holding nothing. Such a block is that spacing, not a paragraph, so it
 * renders as line breaks (`renderSpacing`) and takes no separator on either side; with the
 * separators, the one line Sitecore shows became four or five.
 */
const isSpacing = (block: PortableTextBlock): boolean =>
  isPlain(block) && (block.children ?? []).every(child => textOf(child).trim() === '');

/** A paragraph of text — not a heading, a list item or spacing. */
const isParagraph = (block: PortableTextBlock): boolean => isPlain(block) && !isSpacing(block);

/**
 * Spacing as line breaks: the ones the block holds (Sitecore's `<br />`), and one for an empty block.
 *
 * An empty paragraph does not render as `<p>&nbsp;</p>` on purpose: where the design system shows
 * rich text paragraphs inline (a `RichText` inside a `Text`), that runs the next paragraph onto the
 * same line, while a `<br>` always starts a new one. Between block paragraphs both show one empty line.
 */
const renderSpacing = (block: PortableTextBlock): string => {
  const breaks = (block.children ?? []).map(textOf).join('').split('\n').length - 1;
  return '<br>'.repeat(Math.max(breaks, 1));
};

/**
 * Separator between consecutive paragraphs.
 *
 * The design system renders `<p>` as `<Text as="p">` with **no margin**
 * (`@eneco/components/RichText/RichText`), so paragraphs sit flush and read as one block of text.
 * Sitecore content only looks right because authors typed literal `<br><br>` between them; the
 * HTML→Portable Text conversion drops those, leaving nothing to space migrated paragraphs apart.
 * Reproducing them here keeps the two lanes visually identical, which is what the `?_cms=` comparison
 * is for. A lone `<br />` does survive the conversion, as a block of its own: that block is the
 * spacing, so it takes no separator (`isSpacing`).
 *
 * The better fix is a margin on `Paragraph` in the shared `RichText`, but that component also serves
 * the Sitecore lane, whose content still carries its own `<br>`s — adding one there would double the
 * spacing on every existing page. Worth doing when that lane goes.
 */
const PARAGRAPH_SEPARATOR = '<br><br>';

/**
 * A table cell's text: its one paragraph without `<p>`, as Sitecore's cells hold their text, or else each of its
 * blocks (the schema's cells hold blocks only; every cell in the data holds one paragraph).
 */
const renderCell = (content: unknown): string => {
  const blocks = (Array.isArray(content) ? content : []).filter(
    (item): item is PortableTextBlock => isObject(item) && item._type === 'block',
  );
  const [only] = blocks;
  return blocks.length === 1 && isPlain(only) ? renderChildren(only) : blocks.map(renderBlock).join('');
};

/** A table's rows, each a `<tr>` of cells in the given tag. */
const renderRows = (rows: Record<string, unknown>[], tag: 'td' | 'th'): string =>
  rows
    .map(row => {
      const cells = (Array.isArray(row.cells) ? row.cells : []).filter(isObject);
      const inner = cells.map(cell => `<${tag}>${renderCell(cell.content)}</${tag}>`).join('');
      return `<tr>${inner}</tr>`;
    })
    .join('');

/**
 * A rich text table as Sitecore's rich text holds it: `<table>`, its first row in `<thead>` with `<th>` cells when it
 * is a header row, the rest in `<tbody>` with `<td>` cells. Sitecore's inline styles (borders, padding, a grey header)
 * are not in the CMS data, so the table shows without them.
 */
const renderTable = (member: Record<string, unknown>): string => {
  const rows = (Array.isArray(member.rows) ? member.rows : []).filter(isObject);
  if (rows.length === 0) return '';
  const [first, ...rest] = rows;
  return member.headerRow === true
    ? `<table><thead>${renderRows([first], 'th')}</thead><tbody>${renderRows(rest, 'td')}</tbody></table>`
    : `<table><tbody>${renderRows(rows, 'td')}</tbody></table>`;
};

/** A rich text image as Sitecore's rich text holds it: `<img>` with its alt text and the size it was shown at. */
const renderImage = (member: Record<string, unknown>, toImage?: RichTextImageMapper): string => {
  const image = toImage?.(member);
  if (!image?.src) return '';
  const size = `${dimension('width', member.width)}${dimension('height', member.height)}`;
  return `<img src="${escapeAttr(image.src)}" alt="${escapeAttr(image.alt ?? '')}"${size}>`;
};

/** The members of a rich text field besides its blocks, each written as Sitecore's rich text holds it. */
const MEMBERS = new Map<string, (member: Record<string, unknown>, options: RichTextOptions) => string>([
  ['image', (member, options) => renderImage(member, options.toImage)],
  ['richTextTable', member => renderTable(member)],
]);

/** What `renderBlocks` has written so far, the list it has open, and the block it wrote last. */
interface Writer {
  html: string;
  openList?: 'ol' | 'ul';
  previous?: PortableTextBlock;
}

/** Closes the open list, if there is one. */
const closeList = (writer: Writer): void => {
  if (writer.openList) writer.html += `</${writer.openList}>`;
  writer.openList = undefined;
};

/**
 * A member besides a block, as `MEMBERS` writes it. A member that writes nothing, such as an image without its file,
 * leaves the text around it as it was. Sitecore holds an image right after a list item inside that item
 * (`<li>…<br><img></li>`); the conversion lifted it out of the list. Put back there, it keeps the list whole, so a
 * numbered list does not restart.
 */
const writeMember = (writer: Writer, member: Record<string, unknown>, options: RichTextOptions): void => {
  const written = MEMBERS.get(member._type as string)?.(member, options) ?? '';
  if (!written) return;
  if (member._type === 'image' && writer.openList && writer.previous?.listItem && writer.html.endsWith('</li>')) {
    writer.html = `${writer.html.slice(0, -'</li>'.length)}${written}</li>`;
    return;
  }
  closeList(writer);
  writer.html += written;
  writer.previous = undefined;
};

/**
 * A block: a list item opens its list or goes on in it, any other block closes the open list, and two paragraphs in a
 * row are set apart (`PARAGRAPH_SEPARATOR`).
 */
const writeBlock = (writer: Writer, block: PortableTextBlock): void => {
  const tag = listTagFor(block);
  if (writer.openList !== tag) {
    closeList(writer);
    if (tag) writer.html += `<${tag}>`;
    writer.openList = tag;
  }
  if (writer.previous && isParagraph(writer.previous) && isParagraph(block)) writer.html += PARAGRAPH_SEPARATOR;
  writer.html += isSpacing(block) ? renderSpacing(block) : renderBlock(block);
  writer.previous = block;
};

const renderBlocks = (members: unknown[], options: RichTextOptions): string => {
  const writer: Writer = { html: '' };
  for (const member of members) {
    if (!isObject(member)) continue;
    if (member._type === 'block') writeBlock(writer, member as unknown as PortableTextBlock);
    else writeMember(writer, member, options);
  }
  closeList(writer);
  return writer.html;
};

/**
 * Every part the serializer writes, by the names the schema package gives them, and the ones it leaves out for now.
 * `rich-text-model.spec.ts` compares it with the package's own lists, so a schema build that adds a part fails its
 * import instead of losing that part on the Sanity side.
 */
export const RICH_TEXT_PARTS = {
  decorators: [...DECORATORS.keys()],
  annotations: [...ANNOTATIONS.keys()],
  inlineObjects: [...INLINE_OBJECTS.keys()],
  members: ['block', ...MEMBERS.keys()],
  styles: STYLES,
  notRenderedYet: NOT_RENDERED_YET,
};

/**
 * Serialize Portable Text into the HTML string `RichText` brand the rest of the codebase expects: every part the
 * schema's rich text allows (`RICH_TEXT_PARTS`), each as Sitecore's rich text holds it, so the shared `RichText`
 * renders both lanes alike. The adapter makes one with its image mapper (`createSanityAdapter`).
 *
 * NOTE (A.5 finding): `RichText` is a branded *string*; Sanity rich text is a
 * *block array*. Serializing here is lossy — it cannot express custom Portable
 * Text components (callouts, embeds). Recommend widening `RichText` to
 * `string | PortableTextBlock[]` in Phase A.5 / I rather than serializing.
 */
export const createToRichText =
  (options: RichTextOptions = {}) =>
  (raw: unknown): RichText | undefined => {
    // Already-serialized HTML string (e.g. a legacy/migrated field) passes through.
    if (typeof raw === 'string') return raw.length > 0 ? asRichText(raw) : undefined;
    if (!Array.isArray(raw) || raw.length === 0) return undefined;
    const html = renderBlocks(raw, options);
    return html.length > 0 ? asRichText(html) : undefined;
  };

/** The serializer without an image mapper, for code without an adapter: an image between paragraphs is left out. */
export const toRichText = createToRichText();
